import { useEffect } from 'react';

/** Below this width the phone layout applies (Tailwind's `sm`). */
const MOBILE_QUERY = '(max-width: 639px)';
/** The visual viewport must shrink by at least this much before it counts as an on-screen keyboard. */
const KEYBOARD_MIN_PX = 120;

function isEditable(el: Element | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  if (el.isContentEditable) return true;
  if (el instanceof HTMLTextAreaElement) return !el.readOnly;
  if (el instanceof HTMLInputElement) {
    return !el.readOnly && !['button', 'checkbox', 'radio', 'range', 'color', 'file', 'submit', 'reset'].includes(el.type);
  }
  return false;
}

/**
 * On phones, while the on-screen keyboard is up for an edit inside a Grid (an
 * element marked `data-typing-zone`; text sheets keep their UI), sets `data-keyboard-typing`
 * on <html> and `--typing-viewport-h` to the visible height. CSS in index.css
 * then hides everything marked `data-hide-while-typing` (app header, toolbars,
 * sheet tabs, status bar) so the sheet gets the whole space above the keyboard.
 */
export function useKeyboardTyping(): void {
  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport || typeof window.matchMedia !== 'function') return;
    const mobile = window.matchMedia(MOBILE_QUERY);
    const root = document.documentElement;
    let raf = 0;
    // Tallest visible height seen at the current width. Some browsers shrink the layout viewport
    // (innerHeight) along with the visual one when the keyboard opens, so innerHeight alone can't
    // tell a keyboard apart; a drop from this baseline can. Rotating resets it.
    let baselineWidth = viewport.width;
    let baseline = Math.max(viewport.height, window.innerHeight);

    const update = () => {
      raf = 0;
      const active = document.activeElement;
      if (Math.abs(viewport.width - baselineWidth) > 1) {
        baselineWidth = viewport.width;
        baseline = viewport.height;
      }
      baseline = Math.max(baseline, viewport.height);
      const typing = mobile.matches
        && baseline - viewport.height >= KEYBOARD_MIN_PX
        && isEditable(active)
        && !!active?.closest('[data-typing-zone]')
        // Typing in a toolbar field (search, formula bar) must keep that field on screen.
        && !active.closest('[data-hide-while-typing]');
      if (typing) {
        root.style.setProperty('--typing-viewport-h', `${Math.round(viewport.height)}px`);
        root.style.setProperty('--typing-viewport-top', `${Math.round(viewport.offsetTop)}px`);
        if (!root.hasAttribute('data-keyboard-typing')) {
          root.setAttribute('data-keyboard-typing', '');
          // iOS scrolls the layout viewport to reveal the input; put the sheet back at the top.
          window.scrollTo(0, 0);
        }
      } else if (root.hasAttribute('data-keyboard-typing')) {
        root.removeAttribute('data-keyboard-typing');
        root.style.removeProperty('--typing-viewport-h');
        root.style.removeProperty('--typing-viewport-top');
      }
    };
    const schedule = () => { if (!raf) raf = requestAnimationFrame(update); };

    viewport.addEventListener('resize', schedule);
    // iOS scrolls the page under the keyboard instead of resizing it.
    viewport.addEventListener('scroll', schedule);
    mobile.addEventListener('change', schedule);
    document.addEventListener('focusin', schedule);
    document.addEventListener('focusout', schedule);
    return () => {
      if (raf) cancelAnimationFrame(raf);
      viewport.removeEventListener('resize', schedule);
      viewport.removeEventListener('scroll', schedule);
      mobile.removeEventListener('change', schedule);
      document.removeEventListener('focusin', schedule);
      document.removeEventListener('focusout', schedule);
      root.removeAttribute('data-keyboard-typing');
      root.style.removeProperty('--typing-viewport-h');
      root.style.removeProperty('--typing-viewport-top');
    };
  }, []);
}
